import React, { useEffect, useMemo, useRef } from "react";
import { useIntl } from "react-intl";
import { useSearchParams } from "react-router-dom";
import { Formik } from "formik";
import { GA4_EVENTS, GA4_ONLY_OPTIONS } from "@analytics-setup/events";
import { AnalyticsArea, useTrackEvent } from "@hooks";
import { trackMetaPixelEvent } from "@analytics-setup/marketingPixels";
import Form from "@components/common/form";
import { useLoader, useDemoAccess, useError, useAnalytics } from "@piiqtechnologies/ui-lib";
import messages from "./messages.i18n";
import { useDemoFormFields, useDemoFormValidationSchema, DemoFormInitialValues } from "./DemoFormConfig";
export const DemoForm = ({ onDemoSuccess }) => {
    const intl = useIntl();
    const { setError } = useError();
    const { setLoader } = useLoader();
    const analytics = useAnalytics();
    const track = useTrackEvent();
    const [searchParams] = useSearchParams();
    const autoSubmitTriggeredRef = useRef(false);
    // The success effect below re-runs whenever the parent hands down a new onDemoSuccess, so the
    // ad / GA4 conversion is pinned to the response it belongs to and sent once per granted demo.
    const convertedResponseRef = useRef(null);
    const demoFormFields = useDemoFormFields();
    const demoFormValidationSchema = useDemoFormValidationSchema();
    const [accesstDemo, demoResponse, demoProgress, demoError] = useDemoAccess();
    const [submittedValues, setSubmittedValues] = React.useState(null);
    // Get values from URL params (from free report flow)
    const urlEmail = searchParams.get("email");
    const urlCommunityName = searchParams.get("communityName");
    const initialValues = useMemo(() => {
        return {
            email: urlEmail || DemoFormInitialValues.email,
            name: urlCommunityName || DemoFormInitialValues.name,
            communityName: urlCommunityName || DemoFormInitialValues.communityName,
        };
    }, [urlEmail, urlCommunityName]);
    // Auto-submit if we have all required data from URL params
    useEffect(() => {
        if (autoSubmitTriggeredRef.current)
            return;
        if (!urlEmail || !urlCommunityName)
            return;
        autoSubmitTriggeredRef.current = true;
        const values = {
            email: urlEmail,
            name: urlCommunityName,
            communityName: urlCommunityName,
        };
        setSubmittedValues(values);
        accesstDemo(values);
    }, [urlEmail, urlCommunityName, accesstDemo]);
    useEffect(() => {
        setLoader(demoProgress);
        return () => setLoader(false);
    }, [demoProgress]);
    useEffect(() => {
        if (demoResponse && submittedValues) {
            track(AnalyticsArea.Demo, "Lead Created", {
                email: submittedValues.email,
                name: submittedValues.name,
                communityName: submittedValues.communityName,
            });
            if (convertedResponseRef.current !== demoResponse) {
                convertedResponseRef.current = demoResponse;
                // Deliberately no email / name: GA4 and ad platforms must not receive PII.
                analytics.track(GA4_EVENTS.generateLead, { form: "discover_demo" }, GA4_ONLY_OPTIONS);
                trackMetaPixelEvent("Lead");
            }
            onDemoSuccess(demoResponse);
        }
    }, [demoResponse, submittedValues, analytics, track, onDemoSuccess]);
    const handleSubmit = (values) => {
        setSubmittedValues(values);
        accesstDemo(values);
    };
    useEffect(() => {
        if (demoError === null || demoError === void 0 ? void 0 : demoError.message) {
            setError(demoError === null || demoError === void 0 ? void 0 : demoError.message);
        }
    }, [demoError]);
    return (React.createElement(Formik, { initialValues: initialValues, validationSchema: demoFormValidationSchema, onSubmit: handleSubmit, enableReinitialize: true },
        React.createElement(Form, { error: demoError, formFields: demoFormFields, errorText: intl.formatMessage(messages.demoFormError), submitText: intl.formatMessage(messages.demoButton) })));
};
export default DemoForm;
